/**
 * Cleo Can 54, in a World room: a helper Cleo starts is a session she
 * dispatches (dispatch_provider_targets, create_child_session). It is already
 * a conversation of its own; this derives, from the conversation shells and
 * nothing stored, which of them a parent started, what each was asked, how it
 * is doing and which thread to open. Pure, so the timeline and the Agents panel
 * read the same rows.
 */
import type { OrchestrationThreadShell } from "@cleo/contracts";

export const DISPATCHED_SESSION_STATUSES = [
  "waiting",
  "working",
  "finished",
  "failed",
  "stopped",
  "coding-job",
] as const;
/** Can 81: a coding job's tab is a helper conversation (engine coding-job) its parent started through the World. */
export const CODING_JOB_HELPER_ENGINE = "coding-job";
export type DispatchedSessionStatus = (typeof DISPATCHED_SESSION_STATUSES)[number];

export interface DispatchedSessionRow {
  readonly threadId: string;
  readonly title: string;
  /** What it was asked to do; null when the record kept no ask. */
  readonly ask: string | null;
  readonly status: DispatchedSessionStatus;
  readonly createdAt: string;
}

type ShellFacts = Pick<
  OrchestrationThreadShell,
  | "id"
  | "title"
  | "parentThreadId"
  | "helper"
  | "launchAsk"
  | "createdAt"
  | "session"
  | "latestTurn"
>;

/**
 * A conversation counts as a session its parent started when its parent is
 * named, it is not a native helper's record, and it carries the ask it was
 * launched with. Forks and roots carry no parent; a person's own sub-sessions
 * carry no launch ask.
 */
export function isDispatchedSessionOf(shell: ShellFacts, parentThreadId: string): boolean {
  return (
    shell.parentThreadId === parentThreadId &&
    shell.helper == null &&
    typeof shell.launchAsk === "string" &&
    shell.launchAsk.trim().length > 0
  );
}

/**
 * Can 81: a coding job this conversation started is shown with them too, so its read-only tab opens from where it was
 * asked for. Its live state is in the tab itself (its helper status); the shell carries none, so the row says only that
 * it is a coding job.
 */
export function isCodingJobOf(shell: ShellFacts, parentThreadId: string): boolean {
  return (
    shell.parentThreadId === parentThreadId && shell.helper?.engine === CODING_JOB_HELPER_ENGINE
  );
}

export function dispatchedSessionStatus(
  shell: Pick<ShellFacts, "session" | "latestTurn">,
): DispatchedSessionStatus {
  const live = shell.session?.status;
  if (live === "starting" || live === "running") return "working";
  switch (shell.latestTurn?.state) {
    case "running":
      return "working";
    case "completed":
      return "finished";
    case "error":
      return "failed";
    case "interrupted":
      return "stopped";
    case undefined:
      break;
  }
  if (live === "error") return "failed";
  if (live === "stopped" || live === "interrupted") return "stopped";
  return "waiting";
}

/** The sessions one conversation started, oldest first. */
export function projectDispatchedSessions(
  shells: ReadonlyArray<ShellFacts>,
  parentThreadId: string,
): ReadonlyArray<DispatchedSessionRow> {
  return shells
    .filter(
      (shell) =>
        isDispatchedSessionOf(shell, parentThreadId) || isCodingJobOf(shell, parentThreadId),
    )
    .map((shell) => ({
      threadId: String(shell.id),
      title: shell.title,
      ask: shell.helper != null ? null : (shell.launchAsk ?? null),
      status: shell.helper != null ? ("coding-job" as const) : dispatchedSessionStatus(shell),
      createdAt: shell.createdAt,
    }))
    .sort(
      (left, right) =>
        left.createdAt.localeCompare(right.createdAt) ||
        left.threadId.localeCompare(right.threadId),
    );
}
